// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/Transcript.tsx (AgentTurn)
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { AgentOrb } from '@/components/ui/agent-orb';
import type { OnboardingAgent } from './agents';
import { TURN_ENTRANCE_CLASS } from './UserMessage';

export interface AgentMessageProps {
  agent: OnboardingAgent;
  /** The agent's text, one paragraph per entry (static templates). */
  lines?: readonly string[];
  /** Cards under the text (research card, ads card, ...). They fill the column. */
  children?: ReactNode;
  /** Rise into place (TP's entrance). Off for turns that were already there when the chat opened. Default on. */
  animate?: boolean;
  /** Fade each line in when its text changes (the quiz reply: one line, replaced on every answer). */
  fadeLines?: boolean;
}

/**
 * An agent's turn, flat (no bubble), TP's AgentTurn: a 24px orb centred on the header line, the name in
 * `text-tab`, the role in `text-label text-ds-text-2` (no dot between them), then the body indented under the
 * name (`pl-[34px]`: 24px orb plus the 8px gap and 2px). Cards under the text fill the column.
 */
export function AgentMessage({ agent, lines, children, animate = true, fadeLines = false }: AgentMessageProps) {
  return (
    <div
      data-testid="chat-message"
      data-role="assistant"
      data-agent={agent.id}
      className={cn('group/message flex flex-col gap-2', animate && TURN_ENTRANCE_CLASS)}
    >
      <div data-testid="agent-attribution" className="flex items-baseline gap-2">
        <AgentOrb id={agent.id} size={24} className="self-center" />
        <span className="text-tab text-foreground">{agent.name}</span>
        <span className="text-label text-ds-text-2">{agent.role}</span>
      </div>
      <div className="flex min-w-0 flex-col items-start gap-4 pl-[34px] text-body-sm text-foreground [&>*]:max-w-full [&>section]:w-full">
        {lines && lines.length > 0
          ? lines.map((line, index) => (
              <p
                // The same line can appear twice in one turn; the index keeps keys unique, the text keeps them stable.
                key={`${index}:${line}`}
                data-message-text
                className={cn(
                  'min-w-0 break-words',
                  fadeLines && 'animate-in fade-in duration-300 motion-reduce:animate-none',
                )}
              >
                {line}
              </p>
            ))
          : null}
        {/* The thread is a polite log. `aria-live="off"` keeps a card's own later updates (live counts, spinners, a step
            changing state) out of what it announces: the card arrives with its turn's lines, and a refresh says nothing more. */}
        {children ? (
          <div aria-live="off" className="w-full min-w-0">
            {children}
          </div>
        ) : null}
      </div>
    </div>
  );
}
